import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useHistory } from "react-router-dom";
import { debounce } from "@/other/utils/otherUtils.jsx";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import type { User as AppUser } from "@/bryntum-lib/models/User";
import { useRecentViews } from "@/react/context/recentViewsContext.jsx";
import settingsTemplate from "@/react/components/entityViews/settings/constants/settingsTemplate.json";
import type { SettingsCategory, SettingsData } from "@/react/components/entityViews/settings/constants/settingsStructure";
import { useSettings } from "@/react/components/entityViews/settings/hooks/useSettings";
import { ENTITY_FINDER_DEBOUNCE_MS, ENTITY_FINDER_RECENT_MAX } from "@/modern/components/entity-finder/constants/layout";
import { ENTITY_FINDER_CONFIG, getConfigEntry } from "@/modern/components/entity-finder/constants/config";
import type {
  EntityFinderConfigEntryRuntime,
  EntityFinderContext,
  EntityFinderItem,
  EntityFinderKind,
  EntityFinderListEntry,
  EntityFinderMode,
  EntityFinderViewRecord,
} from "@/modern/components/entity-finder/types";
import {
  buildEntityFinderListEntries,
  collectRecents,
} from "@/modern/components/entity-finder/utils";

interface UseEntityFinderOptions {
  inputRef?: RefObject<HTMLInputElement | null>;
  isFocused?: boolean;
}

function getRecentStores(project: EntityFinderContext["project"]) {
  return [
    project.extraTaskStore,
    project.resourceStore,
    project.clientsStore,
    project.contactsStore,
    project.boardUsersStore,
    project.taskBudgetStore,
    project.checklistStore,
    project.boardNotesStore,
    project.viewsStore,
    project.viewsFolderStore,
  ].filter(Boolean);
}

export function useEntityFinder(options: UseEntityFinderOptions = {}) {
  const { inputRef, isFocused = false } = options;
  const history = useHistory();
  const { project, user, entityManager, editorHandler, viewManager, selectedBoard } =
    useSwivuBryntumContext();
  const { getCategories, getSettings } = useSettings();
  const { currentViews } = useRecentViews();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const isExpandedRef = useRef(false);
  const expandRafRef = useRef<number | null>(null);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);
  const [scopes, setScopes] = useState<EntityFinderKind[]>([]);
  const [viewsRevision, setViewsRevision] = useState(0);
  const [recentsRevision, setRecentsRevision] = useState(0);

  const [settingsCategories, setSettingsCategories] = useState<SettingsCategory[]>([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [settingsReady, setSettingsReady] = useState(false);
  const settingsFetchedRef = useRef(false);

  const currentBoardUser = useMemo(
    () => project?.boardUsersStore?.find((boardUser) => boardUser.userId === user?.id),
    [project, user?.id]
  );

  const isShowArchive = useMemo(() => {
    const selectedBoardSelection = getSavedBoardSelection();
    return getIsArchivedForBoard(selectedBoardSelection?.slug);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setDebouncedQuery("");
      return;
    }

    const timer = window.setTimeout(() => {
      setDebouncedQuery(trimmed);
    }, ENTITY_FINDER_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (settingsFetchedRef.current) return;
    settingsFetchedRef.current = true;

    const templateData = settingsTemplate as unknown as SettingsData;

    const load = async () => {
      setIsLoadingSettings(true);
      try {
        const data = await getSettings();
        setSettingsCategories(getCategories(data, templateData));
      } catch {
        setSettingsCategories(getCategories(null, templateData));
      } finally {
        setIsLoadingSettings(false);
        setSettingsReady(true);
      }
    };

    void load();
  }, [getCategories, getSettings]);

  useEffect(() => {
    if (!project) return;

    const bump = () => setViewsRevision((value) => value + 1);
    const debouncedBump = debounce(bump, 32);
    const detachers = [project.viewsStore, project.viewsFolderStore]
      .filter(Boolean)
      .map((store) => store.on("change", debouncedBump));

    return () => {
      detachers.forEach((detach) => detach());
    };
  }, [project]);

  useEffect(() => {
    if (!project) return;

    const bump = () => setRecentsRevision((value) => value + 1);
    const debouncedBump = debounce(bump, 32);
    const stores = getRecentStores(project);
    const detachers = stores.map((store) => store.on("change", debouncedBump));

    bump();

    return () => {
      detachers.forEach((detach) => detach());
    };
  }, [project]);

  useEffect(() => {
    if (isExpanded && isFocused && project) {
      setRecentsRevision((value) => value + 1);
    }
  }, [isExpanded, isFocused, project]);

  useEffect(() => {
    setRecentsRevision((value) => value + 1);
  }, [currentViews]);

  const finderContext = useMemo((): EntityFinderContext | null => {
    if (!project) return null;

    return {
      project,
      user: user as AppUser | null | undefined,
      entityManager,
      editorHandler: editorHandler as EntityFinderContext["editorHandler"],
      viewManager: viewManager as EntityFinderContext["viewManager"],
      selectedBoard: selectedBoard as EntityFinderContext["selectedBoard"],
      history,
      settingsCategories,
      settingsReady,
      isShowArchive,
      currentBoardUserId: currentBoardUser?.id,
      recentViews: currentViews as unknown as EntityFinderViewRecord[],
      debouncedQuery,
      scopes,
    };
  }, [
    project,
    user,
    entityManager,
    editorHandler,
    viewManager,
    selectedBoard,
    history,
    settingsCategories,
    settingsReady,
    isShowArchive,
    currentBoardUser?.id,
    currentViews,
    debouncedQuery,
    scopes,
    viewsRevision,
    recentsRevision,
  ]);

  const mode: EntityFinderMode = debouncedQuery.trim() ? "search" : "idle";

  const activeEntries = useMemo(() => {
    if (!finderContext) return [];

    return ENTITY_FINDER_CONFIG.filter(
      (entry) => entry.isActive(finderContext, mode) && entry.matchesScope(finderContext)
    );
  }, [finderContext, mode]);

  const groupedResults = useMemo(() => {
    if (!finderContext) return [];

    return activeEntries
      .map((entry) => ({
        entry,
        items: entry.getData(finderContext),
      }))
      .filter((group) => group.items.length > 0);
  }, [finderContext, activeEntries]);

  const listEntries = useMemo((): EntityFinderListEntry[] => {
    if (!finderContext) return [];
    return buildEntityFinderListEntries(groupedResults, finderContext);
  }, [finderContext, groupedResults]);

  const recents = useMemo((): EntityFinderItem[] => {
    if (!finderContext || mode !== "idle") return [];
    return collectRecents(finderContext, ENTITY_FINDER_RECENT_MAX, ENTITY_FINDER_CONFIG);
  }, [finderContext, mode, recentsRevision, scopes]);

  const totalResultCount = useMemo(
    () => groupedResults.reduce((sum, group) => sum + group.items.length, 0),
    [groupedResults]
  );

  const createOnUnmatchedOptions = useMemo(() => {
    if (!finderContext || mode !== "search" || totalResultCount > 0) return [];

    return ENTITY_FINDER_CONFIG.filter((entry) => entry.createOnUnmatched)
      .filter((entry) => entry.createOnUnmatched?.isEnabled?.(finderContext) !== false)
      .map((entry) => ({
        entry,
        label:
          typeof entry.createOnUnmatched!.label === "function"
            ? entry.createOnUnmatched!.label(debouncedQuery)
            : entry.createOnUnmatched!.label,
      }));
  }, [finderContext, mode, debouncedQuery, totalResultCount]);

  const addScope = useCallback((scope: EntityFinderKind) => {
    setScopes((prev) => (prev.includes(scope) ? prev : [...prev, scope]));
  }, []);

  const removeScope = useCallback((scope: EntityFinderKind) => {
    setScopes((prev) => prev.filter((s) => s !== scope));
  }, []);

  const clearScopes = useCallback(() => {
    setScopes([]);
  }, []);

  const selectItem = useCallback(
    (item: EntityFinderItem, configType: EntityFinderKind) => {
      if (!finderContext) return;
      getConfigEntry(configType)?.onSelect(item, finderContext);
    },
    [finderContext]
  );

  const runCreateOnUnmatched = useCallback(
    (entry: EntityFinderConfigEntryRuntime) => {
      if (!finderContext || !entry.createOnUnmatched) return;
      entry.createOnUnmatched.fn(debouncedQuery, finderContext);
    },
    [finderContext, debouncedQuery]
  );

  const expandAndFocus = useCallback(() => {
    isExpandedRef.current = true;
    setIsExpanded(true);
    if (expandRafRef.current != null) {
      cancelAnimationFrame(expandRafRef.current);
    }
    expandRafRef.current = requestAnimationFrame(() => {
      expandRafRef.current = requestAnimationFrame(() => {
        expandRafRef.current = null;
        if (!isExpandedRef.current) return;
        inputRef?.current?.focus();
      });
    });
  }, [inputRef]);

  const collapse = useCallback(() => {
    if (expandRafRef.current != null) {
      cancelAnimationFrame(expandRafRef.current);
      expandRafRef.current = null;
    }
    isExpandedRef.current = false;
    setIsExpanded(false);
    setQuery("");
    setDebouncedQuery("");
    setScopes([]);
  }, []);

  const toggleExpanded = useCallback(() => {
    if (isExpandedRef.current) {
      collapse();
      return false;
    }
    expandAndFocus();
    return true;
  }, [collapse, expandAndFocus]);

  const isSearching = query.trim().length > 0 && query.trim() !== debouncedQuery;

  return {
    query,
    setQuery,
    debouncedQuery,
    listEntries,
    groupedResults,
    recents,
    scopes,
    addScope,
    removeScope,
    clearScopes,
    isSearching,
    isExpanded,
    expandAndFocus,
    collapse,
    toggleExpanded,
    isExpandedRef,
    selectItem,
    createOnUnmatchedOptions,
    runCreateOnUnmatched,
    isLoadingSettings,
    settingsReady,
    finderContext,
    project,
    totalResultCount,
  };
}

export function isEditableTarget(element: Element | null): boolean {
  if (!element) return false;
  const tag = element.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((element as HTMLElement).isContentEditable) return true;
  return Boolean(element.closest("[contenteditable='true']"));
}
